import { forwardRef, useState, useRef, useImperativeHandle } from "react";

import MarkerClusterGroup from "react-leaflet-cluster";
import L, { MarkerCluster } from "leaflet";

import * as S from "./styles";

import { TileLayer, Marker, Circle, Pane } from "react-leaflet";

import { HeatmapLayerFactory } from "@vgrid/react-leaflet-heatmap-layer";

import { IMapLocationData } from "contexts/map/types";

import { allMapMarkerIcon } from "../AllMapMarkerIcon";
import { AllMapDetails } from "../AllMapDetails";
import { useManualOccurrence } from "contexts/register/ManualOccurrence";
import { useGlobalMapActions } from "contexts/map";

import { ReactComponent as LayersIcon } from "assets/layers.svg";
import CloseIcon from "assets/close-small.svg";

const initialCenterPosition = {
  lat: -15.7303544,
  lng: -47.9325513,
};

type Props = {
  markers: IMapLocationData[];
};

const HeatmapLayer = HeatmapLayerFactory<[number, number, number]>();

const createClusterIcon = (cluster: MarkerCluster) => {
  return L.divIcon({
    html: `<S.ClusterMarker>${cluster.getChildCount()}</S.ClusterMarker>`,
    className: "",
    iconSize: L.point(33, 33, true),
  });
};

type MapRef = L.Map | null;

export const AllMap = forwardRef<MapRef, Props>((props, ref) => {
  const { markers } = props;

  const { occurrenceToEdit, setOccurrenceToEditId } = useManualOccurrence();
  const {
    selectedMarker,
    handleMarkerClick,
    activeHeatmap,
    handleHeatmapClick,
    foundHeatmaps,
    originHeatmaps,
  } = useGlobalMapActions();

  const [showRadius, setShowRadius] = useState<boolean>(false);

  const [isLayersOpen, setIsLayersOpen] = useState<boolean>(false);

  const mapRef = useRef<L.Map>(null);
  useImperativeHandle(ref, () => mapRef.current as any);

  function handleLocationClick(marker: IMapLocationData) {
    handleMarkerClick(marker);
    setOccurrenceToEditId(marker.dataId);

    mapRef.current?.setView(
      { lat: marker.coords.lat, lng: marker.coords.lng },
      15
    );
  }

  function handleRadiusClick() {
    if (selectedMarker) {
      const isRadiusShown = !showRadius;
      setShowRadius(isRadiusShown);

      if (isRadiusShown) {
        mapRef.current?.setView(
          { lat: occurrenceToEdit.lastCoordX, lng: occurrenceToEdit.lastCoordY },
          12
        );
      }
    }
  }

  return (
    <S.Wrapper>
      <S.StyledMapContainer
        center={initialCenterPosition}
        scrollWheelZoom
        zoom={4}
        ref={mapRef}
      >
        {activeHeatmap !== undefined && (
          <HeatmapLayer
            points={activeHeatmap === "found" ? foundHeatmaps : originHeatmaps}
            latitudeExtractor={(el) => el[0]}
            longitudeExtractor={(el) => el[1]}
            intensityExtractor={(el) => el[2]}
          />
        )}

        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />

        <MarkerClusterGroup
          chunkedLoading
          iconCreateFunction={createClusterIcon}
          showCoverageOnHover={false}
        >
          {activeHeatmap === undefined &&
            markers.map((marker) => (
              <Marker
                icon={allMapMarkerIcon({
                  type: marker.filterType,
                })}
                position={{
                  lat: marker.coords.lat,
                  lng: marker.coords.lng,
                }}
                eventHandlers={{
                  click: () => {
                    handleLocationClick(marker);
                  },
                }}
              />
            ))}
        </MarkerClusterGroup>

        <Pane name="global-map-pane">
          {selectedMarker && showRadius && (
            <Circle
              center={[selectedMarker.coords.lat, selectedMarker.coords.lng]}
              radius={selectedMarker.searchRadius}
              pane="global-map-pane"
              color="var(--black-300)"
            />
          )}
        </Pane>

        {selectedMarker &&
          (selectedMarker.type === "patrimony" ||
            selectedMarker.type === "vehicle") && (
            <S.FooterMapContainer>
              <AllMapDetails
                onShowRadiusClick={handleRadiusClick}
                onRequestClose={() => handleMarkerClick(undefined)}
              />
            </S.FooterMapContainer>
          )}

        <S.LayerButtonContainer>
          <button
            onClick={() => {
              setIsLayersOpen(!isLayersOpen);
            }}
          >
            <LayersIcon />
          </button>

          {isLayersOpen && (
            <S.HeatmapContainer>
              <S.HeatmapHeader>
                <h3>Selecionar camada</h3>
                <S.CloseButton
                  type="button"
                  onClick={() => {
                    setIsLayersOpen(false);
                  }}
                >
                  <img src={CloseIcon} alt="Botão de fechar o pop-up" />
                </S.CloseButton>
              </S.HeatmapHeader>

              <label>Marcadores</label>

              <S.ButtonActiveMap
                activeHeatmap={activeHeatmap === undefined}
                onClick={() => handleHeatmapClick(undefined)}
              >
                Posições no mapa
              </S.ButtonActiveMap>

              <label>Mapas de calor (veicular)</label>

              <S.ButtonActiveMap
                activeHeatmap={activeHeatmap === "not-found"}
                onClick={() => handleHeatmapClick("not-found")}
              >
                Origem (primeira localização)
              </S.ButtonActiveMap>

              <S.ButtonActiveMap
                activeHeatmap={activeHeatmap === "found"}
                onClick={() => handleHeatmapClick("found")}
              >
                Destino (onde são encontrados)
              </S.ButtonActiveMap>
            </S.HeatmapContainer>
          )}
        </S.LayerButtonContainer>
      </S.StyledMapContainer>
    </S.Wrapper>
  );
});
